import React, { useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Row,
  Col,
  Divider,
  Alert,
  Progress,
  Tooltip,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  KeyOutlined,
  TeamOutlined,
  WarningOutlined,
  CrownOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
  useGetRolesQuery,
  useGetUserLimitInfoQuery,
  User,
} from '../services/usersApi';
import { isAdmin, isSupportUser } from '../utils/roleHelpers';
import {
  BUY_EXTRA_USERS_PATH,
  isPaidPlan,
  isProfessionalPlan,
  planDisplayName,
  SUPPORT_WHATSAPP_URL,
} from '../constants/moduleKeys';
import { useGetRestaurantsQuery } from '../services/restaurantsApi';
import { useAppSelector } from '../hooks/redux';
import AvatarIconPicker from '../components/common/AvatarIconPicker';
import UserAvatar, { getUserInitial } from '../components/common/UserAvatar';
import { sanitizeAvatarIcon } from '../constants/avatarIcons';

const { Title } = Typography;

const UserManagement: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { message } = App.useApp(); // Use App context for proper message display
  const { user } = useAppSelector((state) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();

  // La opción "sin icono" del selector muestra la inicial de quien se está
  // editando, para que se vea exactamente cómo va a quedar el avatar. El correo
  // sale de editingUser y no de otro useWatch: es el respaldo de la cadena, y
  // suscribirse a un segundo campo re-renderizaría la página con cada tecla que
  // se escriba en él.
  const watchedFirstName = Form.useWatch<string | undefined>('firstName', form);
  const avatarInitial = getUserInitial(watchedFirstName, editingUser?.email);

  // Multi-restaurante: el par de selects solo aparece cuando la org tiene 2+
  // restaurantes activos; con uno, el backend asigna el actual como siempre.
  const { data: orgRestaurants = [] } = useGetRestaurantsQuery();
  const activeRestaurants = orgRestaurants.filter((r) => r.isActive);
  const isMultiRestaurant = activeRestaurants.length > 1;
  const watchedRestaurantIds = Form.useWatch<string[] | undefined>('restaurantIds', form);

  // Check if current user is admin
  const userIsAdmin = isAdmin(user?.role) || isSupportUser(user?.email);

  // API hooks
  const { data: users = [], isLoading: loadingUsers } = useGetUsersQuery();
  const { data: roles = [] } = useGetRolesQuery();
  const { data: limitInfo } = useGetUserLimitInfoQuery();

  /**
   * Botón para conseguir más lugares. Con plan de paga al corriente y una sola sucursal se
   * compran en Suscripción; con varias sucursales los agrega soporte (la compra se niega
   * ahí, porque el extra sumaría un lugar en cada una); en prueba o con el pago vencido
   * primero hay que contratar o regularizar.
   */
  const renderMoreUsersButton = () => {
    const paid = isPaidPlan(limitInfo?.plan) && !limitInfo?.isTrialExpired;
    if (paid && isMultiRestaurant) {
      return (
        <Button type="primary" href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          Pedir más usuarios
        </Button>
      );
    }
    if (paid) {
      return (
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => navigate(BUY_EXTRA_USERS_PATH)}>
          Comprar usuarios
        </Button>
      );
    }
    return (
      <Button type="primary" onClick={() => navigate('/subscription')}>
        Mejorar Plan
      </Button>
    );
  };
  const [createUser, { isLoading: creating }] = useCreateUserMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [resetPassword, { isLoading: resettingPassword }] = useResetPasswordMutation();

  const loading = creating || updating;

  // Calculate usage percentage
  const usagePercent = limitInfo ? Math.round((limitInfo.currentUsers / limitInfo.maxUsers) * 100) : 0;
  const isAtLimit = limitInfo ? limitInfo.currentUsers >= limitInfo.maxUsers : false;
  const canCreate = limitInfo?.canCreateUsers ?? true;

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    // El icono se sanea al cargar: si viene una clave que este catálogo ya no
    // dibuja, el form la reenviaría tal cual al guardar y el backend la rechaza.
    // Solo membresías de sucursales activas: una cancelada precargada dejaba una
    // opción sin nombre en el Select y el backend rechazaba el guardado completo.
    const activeIds = new Set(activeRestaurants.map((r) => r.id));
    const memberIds = (user.restaurantIds ?? []).filter((id) => activeIds.has(id));
    const primary = user.primaryRestaurantId && activeIds.has(user.primaryRestaurantId)
      ? user.primaryRestaurantId
      : memberIds[0];
    form.setFieldsValue({
      ...user,
      avatarIcon: sanitizeAvatarIcon(user.avatarIcon),
      restaurantIds: memberIds,
      primaryRestaurantId: primary,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteUser(userId).unwrap();
      message.success('Usuario eliminado correctamente');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para eliminar usuarios'
        : error?.data?.message || 'Error al eliminar el usuario';
      message.error(errorMsg, 5);
    }
  };

  const handleResetPassword = (user: User) => {
    setResetPasswordUser(user);
    resetPasswordForm.resetFields();
    setIsResetPasswordModalOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordUser) return;

    try {
      const values = await resetPasswordForm.validateFields();
      await resetPassword({
        id: resetPasswordUser.id,
        newPassword: values.newPassword,
      }).unwrap();
      message.success('Contraseña restablecida correctamente');
      setIsResetPasswordModalOpen(false);
      resetPasswordForm.resetFields();
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMsg = error?.status === 403
        ? 'No tienes permisos para restablecer contraseñas'
        : error?.data?.message || 'Error al restablecer la contraseña';
      message.error(errorMsg, 5);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        await updateUser({
          id: editingUser.id,
          ...values,
        }).unwrap();
        message.success('Usuario actualizado correctamente');
      } else {
        await createUser(values).unwrap();
        message.success('Usuario creado correctamente');
      }

      setIsModalOpen(false);
      form.resetFields();
    } catch (error: any) {
      console.error('Error saving user:', error);

      // Check if this is a form validation error (not an API error)
      if (error?.errorFields) {
        // Form validation error - don't show additional message, Ant Design handles it
        return;
      }

      // API error handling
      let errorMsg = 'Error al guardar el usuario';

      if (error?.status === 403) {
        errorMsg = 'No tienes permisos para ' + (editingUser ? 'editar' : 'crear') + ' usuarios';
      } else if (error?.status === 400 || error?.status === 409) {
        // Handle both 400 and 409 for duplicate email
        errorMsg = error?.data?.message || 'Los datos del usuario son inválidos';
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      }

      message.error(errorMsg, 5);
    }
  };

  const getRoleColor = (role: string) => {
    const roleColors: Record<string, string> = {
      Admin: 'red',
      Manager: 'orange',
      Waiter: 'blue',
      Chef: 'green',
      'Cajero/Mesero': 'purple',
    };
    return roleColors[role] || 'default';
  };

  const columns = [
    {
      title: 'Nombre',
      key: 'name',
      // Con el avatar al lado, la lista se lee igual que el chip del header:
      // quien asigna los iconos ve de inmediato si dos personas quedaron iguales.
      render: (_: any, record: User) => (
        <Space size={10}>
          <UserAvatar
            size={28}
            firstName={record.firstName}
            email={record.email}
            avatarIcon={record.avatarIcon}
          />
          <span>{`${record.firstName} ${record.lastName}`}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <Tag color={getRoleColor(role)}>{role}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {!isMobile && 'Editar'}
          </Button>
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record)}
            size="small"
          >
            {!isMobile && 'Resetear'}
          </Button>
          <Popconfirm
            title="¿Eliminar usuario?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.id)}
            okText="Eliminar"
            cancelText="Cancelar"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              {!isMobile && 'Eliminar'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Mobile-optimized user cards
  const renderUserCards = () => (
    <Row gutter={[16, 16]}>
      {users.map((user: User) => (
        <Col xs={24} sm={12} md={8} key={user.id}>
          <Card
            size="small"
            title={`${user.firstName} ${user.lastName}`}
            extra={
              <Tag color={user.isActive ? 'green' : 'red'}>
                {user.isActive ? 'Activo' : 'Inactivo'}
              </Tag>
            }
            actions={[
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(user)}
                size="small"
              >
                Editar
              </Button>,
              <Button
                type="link"
                icon={<KeyOutlined />}
                onClick={() => handleResetPassword(user)}
                size="small"
              >
                Resetear
              </Button>,
              <Popconfirm
                title="¿Eliminar usuario?"
                description="Esta acción no se puede deshacer"
                onConfirm={() => handleDelete(user.id)}
                okText="Eliminar"
                cancelText="Cancelar"
              >
                <Button type="link" danger icon={<DeleteOutlined />} size="small">
                  Eliminar
                </Button>
              </Popconfirm>,
            ]}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Typography.Text type="secondary">Email:</Typography.Text>
                <br />
                <Typography.Text>{user.email}</Typography.Text>
              </div>
              <div>
                <Typography.Text type="secondary">Rol:</Typography.Text>
                <br />
                <Tag color={getRoleColor(user.role)}>{user.role}</Tag>
              </div>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <div>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Volver a Configuración
      </Button>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <UserOutlined /> Gestión de Usuarios
        </Title>
      </div>

      {/* User Limit Info Card */}
      {limitInfo && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <TeamOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <Typography.Text strong>Usuarios</Typography.Text>
                </Space>
                <Progress
                  percent={usagePercent}
                  size="small"
                  status={isAtLimit ? 'exception' : 'active'}
                  format={() => `${limitInfo.currentUsers} / ${limitInfo.maxUsers}`}
                />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <CrownOutlined style={{ fontSize: 16, color: '#faad14' }} />
                <Typography.Text>
                  Plan: <Tag color="blue">{limitInfo.plan}</Tag>
                </Typography.Text>
              </Space>
            </Col>
            {userIsAdmin && isAtLimit && (
              <Col xs={24} md={8}>
                {renderMoreUsersButton()}
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* Trial Expired Alert */}
      {limitInfo?.isTrialExpired && (
        <Alert
          message={
            <Space>
              <WarningOutlined />
              <strong>Periodo de prueba expirado</strong>
            </Space>
          }
          description={
            userIsAdmin ? (
              <Space direction="vertical">
                <span>Tu periodo de prueba ha terminado. Activa una suscripción para poder crear más usuarios.</span>
                <Button type="primary" onClick={() => navigate('/subscription')}>
                  Ver Planes
                </Button>
              </Space>
            ) : (
              <span>Tu periodo de prueba ha terminado. Contacta al administrador para activar una suscripción.</span>
            )
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* User Limit Warning */}
      {!limitInfo?.isTrialExpired && isAtLimit && (
        <Alert
          message="Límite de usuarios alcanzado"
          description={
            userIsAdmin ? (
              <Space direction="vertical">
                <span>
                  {limitInfo && limitInfo.currentUsers > limitInfo.maxUsers
                    // Plan que bajó (o prueba que terminó en Básico): nadie pierde el
                    // acceso, pero hay más usuarios que asientos. Decirlo tal cual. El
                    // conteo es de ESTA sucursal: cada sucursal tiene su propio cupo.
                    ? `Tu plan ${planDisplayName(limitInfo.plan)} incluye ${limitInfo.maxUsers} usuario(s) por sucursal y en esta tienes ${limitInfo.currentUsers}. Tus usuarios siguen entrando, pero no puedes dar de alta más aquí.`
                    : `Esta sucursal ya tiene los ${limitInfo?.maxUsers} usuario(s) de tu plan ${planDisplayName(limitInfo?.plan)}.`}
                </span>
                <Space wrap>
                  {renderMoreUsersButton()}
                  {/* Profesional es el plan más grande: "mejorar" no lleva a ningún lado. */}
                  {isPaidPlan(limitInfo?.plan) && !isProfessionalPlan(limitInfo?.plan) && (
                    <Button onClick={() => navigate('/subscription')}>
                      Mejorar Plan
                    </Button>
                  )}
                </Space>
              </Space>
            ) : (
              <span>Se ha alcanzado el límite de usuarios. Contacta al administrador para mejorar el plan.</span>
            )
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Tooltip title={!canCreate ? 'No puedes crear más usuarios con tu plan actual' : ''}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              size={isMobile ? 'middle' : 'large'}
              block={isMobile}
              disabled={!canCreate}
            >
              Agregar Usuario
            </Button>
          </Tooltip>
        </div>

        {isMobile ? (
          loadingUsers ? (
            <Card loading={true} />
          ) : (
            renderUserCards()
          )
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            loading={loadingUsers}
            rowKey="id"
            scroll={{ x: 800 }}
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: 'No hay usuarios registrados',
            }}
          />
        )}
      </Card>

      <Modal
        title={editingUser ? 'Editar Usuario' : 'Agregar Usuario'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={loading}
        width={isMobile ? '90%' : 600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="firstName"
            label="Nombre"
            rules={[{ required: true, message: 'Ingrese el nombre' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Apellido"
            rules={[{ required: true, message: 'Ingrese el apellido' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingrese el email' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input type="email" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Contraseña"
              rules={[
                { required: true, message: 'Ingrese la contraseña' },
                { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="Rol"
            rules={[{ required: true, message: 'Seleccione un rol' }]}
          >
            <Select
              placeholder="Seleccione un rol"
              options={roles.map((r) => ({ label: r.name, value: r.name }))}
            />
          </Form.Item>

          {isMultiRestaurant && (
            <>
              <Form.Item
                name="restaurantIds"
                label="Restaurantes"
                rules={[{ required: true, message: 'Seleccione al menos un restaurante' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Restaurantes a los que tendrá acceso"
                  options={activeRestaurants.map((r) => ({ label: r.name, value: r.id }))}
                  onChange={(ids: string[]) => {
                    // Si el principal quedó fuera de la selección, se limpia para
                    // que el validador lo pida de nuevo.
                    const primary = form.getFieldValue('primaryRestaurantId');
                    if (primary && !ids.includes(primary)) {
                      form.setFieldValue('primaryRestaurantId', undefined);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="primaryRestaurantId"
                label="Restaurante principal"
                tooltip="Con este restaurante abre sesión por defecto"
                rules={[{ required: true, message: 'Seleccione el restaurante principal' }]}
              >
                <Select
                  placeholder="Con cuál inicia sesión"
                  options={activeRestaurants
                    .filter((r) => (watchedRestaurantIds ?? []).includes(r.id))
                    .map((r) => ({ label: r.name, value: r.id }))}
                />
              </Form.Item>
            </>
          )}

          {/* El icono va DENTRO del form a propósito: este PUT reemplaza al
              usuario completo, así que si el campo no viajara, editar a alguien
              le borraría el icono que se hubiera puesto. */}
          <Form.Item name="avatarIcon" label="Icono de avatar">
            <AvatarIconPicker initial={avatarInitial} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title="Restablecer Contraseña"
        open={isResetPasswordModalOpen}
        onOk={handleResetPasswordSubmit}
        onCancel={() => {
          setIsResetPasswordModalOpen(false);
          resetPasswordForm.resetFields();
        }}
        confirmLoading={resettingPassword}
        width={isMobile ? '90%' : 500}
        okText="Restablecer"
        cancelText="Cancelar"
      >
        {resetPasswordUser && (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Usuario:</Typography.Text> {resetPasswordUser.firstName} {resetPasswordUser.lastName}
            </div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Email:</Typography.Text> {resetPasswordUser.email}
            </div>
            <Divider />
          </>
        )}
        <Form form={resetPasswordForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="Nueva Contraseña"
            rules={[
              { required: true, message: 'Ingrese la nueva contraseña' },
              { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' },
            ]}
          >
            <Input.Password placeholder="Ingrese la nueva contraseña" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirmar Contraseña"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirme la nueva contraseña' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Las contraseñas no coinciden'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirme la nueva contraseña" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
